import { ChangeRecord, IndexedStorage } from './indexed-storage';
import { QueryableStorageIndexProxy } from './queryable-storage-index-proxy';
import { StorageEmitter } from './storage-emitter';
import { debounce } from 'lodash';

// eslint-disable-next-line unused-imports/no-unused-vars -- For typed extendability.
export interface QueryStorage<T> {
    /**
     * Optional filter by indexed values.
     *
     * This is performance-optimized for indexed values only;
     * if you need to filter by non-indexed values, do so with a normal `.filter()` on the results.
     */
    index?: QueryStorageIndexPredicate;
    /**
     * Optional limit on number of records to return.
     *
     * If specified, you must call {@link StorageEmitter.more()} until `no-more` is emitted.
     */
    limit?: number;
}

export interface QueryStorageIndexPredicate {
    isMatch(index: string | undefined, id: string): boolean;
}
export interface PruneStorageSortValueAction {
    getSortValue(index: string | undefined, id: string): string;
}

export class QueryableStorage<T extends { id: string }> {
    public readonly index: QueryableStorageIndexProxy<T>;

    constructor(
        private readonly name: string,
        private readonly _index: IndexedStorage<T>,
    ) {
        this.index = new QueryableStorageIndexProxy(_index);
    }

    /**
     * Opens a live query: a full `load` now and after every partition change, then per-record changes.
     * Loads run one at a time; changes that arrive while one is reading are held and applied after it lands, so its older snapshot cannot drop them.
     */
    query(query: QueryStorage<T> = {}): StorageEmitter<T> {
        const emitter = new StorageEmitter<T>();
        let held: Array<ChangeRecord<T>> | null = null;
        let loading = false;
        let reloadQueued = false;

        const apply = (value: ChangeRecord<T>) => {
            try {
                if (value.delete) emitter.deleted(value.value.id);
                else emitter.next(value);
            } catch (e) {
                console.error(`Failed to apply a ${this.name} change to ${value.value.id}`, e);
            }
        };

        const loadOnce = async () => {
            held = [];
            const start = Date.now();
            try {
                const list = await this.pull(query, emitter);
                try {
                    emitter.load(list);
                } catch (e) {
                    console.error(`A ${this.name} load listener failed`, e);
                }
            } finally {
                const pending = held ?? [];
                held = null;
                if (pending.length) console.log(`Applying ${pending.length} ${this.name} changes held ${Date.now() - start} ms by a load`);
                pending.forEach(value => apply(value));
            }
        };

        const load = async () => {
            if (loading) {
                reloadQueued = true;
                return;
            }
            loading = true;
            do {
                reloadQueued = false;
                try {
                    await loadOnce();
                } catch (e) {
                    console.error(`Failed to load ${this.name} query`, e);
                }
            } while (reloadQueued);
            loading = false;
        };

        const debounced = debounce(() => void load(), 100);
        const subs = [
            this._index.changed$.subscribe(value => {
                if (held) held.push(value);
                else apply(value);
            }),
            this._index.partitionChanged$.subscribe(() => {
                emitter.loading();
                debounced();
            }),
        ];
        emitter.on('close', () => {
            subs.forEach(x => x.unsubscribe());
        });

        // purposefully not awaiting. results are retrieved though emitter.
        void load();

        return emitter;
    }

    /** Reads every record the query matches; the caller loads them into the emitter. */
    private async pull(
        query: QueryStorage<T>,
        emitter: StorageEmitter<T>,
    ) {
        const list: T[] = [];

        const index = await this._index.getIndex();

        let lastIndex = 0;
        const getNextValues = async (keys: string[]) => {
            let remaining = query.limit || keys.length;
            const results = [];
            while (remaining > 0 && lastIndex < keys.length) {
                const end = Math.min(keys.length, lastIndex + remaining);
                const found = await this._index.getAll(keys.slice(lastIndex, end));
                results.push(...found);
                remaining -= found.length;
                lastIndex = end;
            }
            return results;
        };

        if (query.index) {
            const keys = Object.keys(index)
                .sort((a, b) => ((index[a] ?? '') < (index[b] ?? '') ? 1 : -1))
                .filter(key => query.index!.isMatch(index[key], key));

            if (query.limit) {
                const refresh = async () => {
                    const remaining = await getNextValues(keys);
                    if (remaining.length) {
                        emitter.load(remaining, true);
                    } else {
                        emitter.emit('no-more');
                    }
                };
                emitter.on('request-more', refresh);
                list.push(...(await getNextValues(keys)));
            } else {
                list.push(...(await getNextValues(keys)));
            }
        } else {
            const keys = Object.keys(index);
            const values = await getNextValues(keys);
            list.push(...values);
        }
        return list;
    }

    async prune(maxRecords: number, action: PruneStorageSortValueAction): Promise<Set<string>> {
        const index = await this._index.getIndex();
        const keys = Object.keys(index)
            .map(key => ({
                id: key,
                sortValue: action.getSortValue(index[key], key),
            }));
        if (keys.length < maxRecords * 0.75) return new Set();

        const toPrune = keys.length - maxRecords * 0.5;
        console.warn(`Pruning ${toPrune} ${this.name} cache because too many items ${keys.length}`);

        const toRemove = new Set(keys
            .sort((a, b) => a.sortValue < b.sortValue ? -1 : 1)
            .slice(0, toPrune)
            .map(x => x.id));

        return await this._index.removeMany(id => toRemove.has(id));
    }
}
